import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Include cookies for session management
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on 401 for auth check requests - let the AuthContext handle it
    // Don't redirect on 401 for embed pages - they should handle auth gracefully
    // Don't redirect on 401 for developer/me or organizations (handled by contexts)
    const url = error.config?.url || '';
    const pathname = window.location.pathname;
    
    const isAuthCheck = url.includes('/auth/me') || url.includes('/developer/me');
    const isEmbedPage = pathname.startsWith('/embed/');
    const isOrganizationCheck = url.includes('/organizations');
    const isLoginPage = pathname.includes('/login') || pathname.includes('/register');
    const isDeveloperRoute = pathname.startsWith('/developer');
    
    // Never redirect if:
    // - It's an auth check endpoint
    // - We're on an embed page
    // - We're on a login/register page
    // - It's an organization check (OrganizationContext handles this)
    // - We're already on a developer route
    if (error.response?.status === 401 && 
        !isAuthCheck && 
        !isEmbedPage && 
        !isOrganizationCheck && 
        !isLoginPage && 
        !isDeveloperRoute) {
      // Only redirect on other 401 errors
      // Prevent infinite redirects
      if (pathname !== '/developer/login' && pathname !== '/developer/register') {
        window.location.href = '/developer/login';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const auth = {
  login: (password, recaptchaToken) => api.post('/auth/login', { password, recaptcha_token: recaptchaToken }),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
};

// Agents API
export const agents = {
  list: (archived = false) => api.get(`/agents?archived=${archived}`),
  create: (agentData) => api.post('/agents', agentData),
  get: (id) => api.get(`/agents/${id}`),
  update: (id, agentData) => api.put(`/agents/${id}`, agentData),
  archive: (id) => api.post(`/agents/${id}/archive`),
  unarchive: (id) => api.post(`/agents/${id}/unarchive`),
  delete: (id) => api.delete(`/agents/${id}`),
  // Data Extraction Fields
  listExtractionFields: (agentId) => api.get(`/agents/${agentId}/extraction-fields`),
  createExtractionField: (agentId, fieldData) => api.post(`/agents/${agentId}/extraction-fields`, fieldData),
  updateExtractionField: (agentId, fieldId, fieldData) => api.put(`/agents/${agentId}/extraction-fields/${fieldId}`, fieldData),
  deleteExtractionField: (agentId, fieldId) => api.delete(`/agents/${agentId}/extraction-fields/${fieldId}`),
};

// Sessions API
export const sessions = {
  list: (page = 1, limit = 50) => api.get(`/sessions?page=${page}&limit=${limit}`),
  stats: () => api.get('/sessions/stats'),
  usage: (period = 'week', startDate = null, endDate = null) => {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.append('start_date', startDate);
      params.append('end_date', endDate);
    } else {
      params.append('period', period);
    }
    return api.get(`/sessions/usage?${params.toString()}`);
  },
  create: (agentId) => api.post('/sessions/web', { agent_id: agentId }),
  end: (sessionId) => api.post(`/sessions/${sessionId}/end`),
  heartbeat: (sessionId) => api.post(`/sessions/${sessionId}/heartbeat`),
  cleanup: () => api.post('/sessions/cleanup'),
  health: () => api.get('/sessions/health'),
  // Phase 4: New session management endpoints
  injectApi: (sessionId, data) => api.post(`/sessions/${sessionId}/inject-api`, data),
  transfer: (sessionId, data) => api.post(`/sessions/${sessionId}/transfer`, data),
  analyze: (sessionId, data) => api.post(`/sessions/${sessionId}/analyze`, data),
  getAnalysis: (sessionId) => api.get(`/sessions/${sessionId}/analysis`),
  // Re-signs on every request server-side -- use this for playback instead of the raw
  // recording_url field, which can carry a stale/expired signature for old recordings.
  recordingUrl: (sessionId) => `${API_BASE_URL}/sessions/${sessionId}/recording`,
  callHistory: (agentId, startDate, endDate, page = 1, limit = 50) => {
    const params = new URLSearchParams({
      agent_id: agentId,
      start_date: startDate,
      end_date: endDate,
      page: page.toString(),
      limit: limit.toString(),
    });
    return api.get(`/sessions/call-history?${params.toString()}`);
  },
  chatHistory: (agentId, startDate, endDate, page = 1, limit = 50) => {
    const params = new URLSearchParams({
      agent_id: agentId,
      start_date: startDate,
      end_date: endDate,
      page: page.toString(),
      limit: limit.toString(),
      channel: 'chat',
    });
    return api.get(`/sessions/call-history?${params.toString()}`);
  },
};

// Phone numbers API
export const phoneNumbers = {
  list: () => api.get('/numbers'),
  create: (e164, agentId) => api.post('/numbers', { e164, agent_id: agentId }),
  setup: (data) => api.post('/numbers/setup', data),
  setupTelnyx: (agentId, telnyxPhoneNumber) => api.post('/numbers/setup/telnyx', { 
    agent_id: agentId, 
    telnyx_phone_number: telnyxPhoneNumber 
  }),
  delete: (id) => api.delete(`/numbers/${id}`),
};

// Telnyx-specific API
export const telnyx = {
  getDebugStatus: () => api.get('/debug/telnyx/status'),
  setupPhoneNumber: (agentId, telnyxPhoneNumber) => api.post(`/numbers/setup/telnyx?agent_id=${encodeURIComponent(agentId)}&telnyx_phone_number=${encodeURIComponent(telnyxPhoneNumber)}`),
  searchAvailableNumbers: ({ countryCode = 'US', areaCode, contains, numberType = 'local', limit = 20 } = {}) => {
    const params = new URLSearchParams({ country_code: countryCode, number_type: numberType, limit });
    if (areaCode) params.append('area_code', areaCode);
    if (contains) params.append('contains', contains);
    return api.get(`/numbers/telnyx/available?${params.toString()}`);
  },
  buyAndSetup: (agentId, phoneNumber) =>
    api.post(`/numbers/telnyx/buy-and-setup?agent_id=${encodeURIComponent(agentId)}&phone_number=${encodeURIComponent(phoneNumber)}`),
};

// Vonage-specific API
export const vonage = {
  searchAvailableNumbers: ({ countryCode = 'US', areaCode, contains, numberType = 'local', limit = 20 } = {}) => {
    const params = new URLSearchParams({ country_code: countryCode, number_type: numberType, limit });
    if (areaCode) params.append('area_code', areaCode);
    if (contains) params.append('contains', contains);
    return api.get(`/numbers/vonage/available?${params.toString()}`);
  },
  buyAndSetup: (agentId, phoneNumber, country = 'US') =>
    api.post(
      `/numbers/vonage/buy-and-setup?agent_id=${encodeURIComponent(agentId)}&phone_number=${encodeURIComponent(phoneNumber)}&country=${encodeURIComponent(country)}`
    ),
  setupExisting: (agentId, vonagePhoneNumber, country = 'US') =>
    api.post(
      `/numbers/setup/vonage?agent_id=${encodeURIComponent(agentId)}&vonage_phone_number=${encodeURIComponent(vonagePhoneNumber)}&country=${encodeURIComponent(country)}`
    ),
};

// Twilio-specific API
export const twilio = {
  getDebugStatus: () => api.get('/debug/twilio/status'),
  searchAvailableNumbers: ({ countryCode = 'US', areaCode, contains, numberType = 'local', limit = 20 } = {}) => {
    const params = new URLSearchParams({ country_code: countryCode, number_type: numberType, limit });
    if (areaCode) params.append('area_code', areaCode);
    if (contains) params.append('contains', contains);
    return api.get(`/numbers/twilio/available?${params.toString()}`);
  },
  buyAndSetup: (agentId, phoneNumber, countryCode = 'US') =>
    api.post(
      `/numbers/twilio/buy-and-setup?agent_id=${encodeURIComponent(agentId)}&phone_number=${encodeURIComponent(phoneNumber)}&country_code=${encodeURIComponent(countryCode)}`
    ),
  setupExisting: (agentId, twilioPhoneNumber) =>
    api.post(
      `/numbers/setup/twilio?agent_id=${encodeURIComponent(agentId)}&twilio_phone_number=${encodeURIComponent(twilioPhoneNumber)}`
    ),
};

// Phase 4: Webhook Configuration API
export const webhooks = {
  getConfig: () => api.get('/webhooks/config'),
  updateConfig: (data) => api.post('/webhooks/config', data),
  listEvents: (page = 1, limit = 20, filters = {}) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    
    // Add filters if they exist
    if (filters.eventType) params.append('event_type', filters.eventType);
    if (filters.status) params.append('status', filters.status);
    
    return api.get(`/webhooks/events?${params.toString()}`);
  },
  getStatistics: () => api.get('/webhooks/events/statistics'),
  testOutbound: (data) => api.post('/webhooks/outbound/test', data),
  handleInbound: (data) => api.post('/webhooks/inbound', data),
};

// Phase 4: Function Registry API
export const functions = {
  list: () => api.get('/functions'),
};

// Phase 4: Custom Functions Management API
export const customFunctions = {
  list: () => api.get('/custom-functions'),
  create: (data) => api.post('/custom-functions', data),
  update: (id, data) => api.put(`/custom-functions/${id}`, data),
  delete: (id) => api.delete(`/custom-functions/${id}`),
  test: (id, data) => api.post(`/custom-functions/${id}/test`, data),
};

// Phase 4: Cost Analytics API
export const costs = {
  analytics: (days = 30) => api.get(`/sessions/costs?days=${days}`),
};

// Phase 7: Knowledge Base API
export const knowledgeBase = {
  uploadDocument: (formData) => api.post('/knowledge-base/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  listDocuments: (params = {}) => api.get('/knowledge-base/documents', { params }),
  deleteDocument: (docId) => api.delete(`/knowledge-base/documents/${docId}`),
  // Wipes a whole Pinecone namespace. Body: { agent_id?, confirm: 'DELETE' }
  purge: (data) => api.post('/knowledge-base/purge', data),
  reindexDocument: (docId) => api.post(`/knowledge-base/documents/${docId}/reindex`),
  query: (data) => api.post('/knowledge-base/query', data),
  getStats: () => api.get('/knowledge-base/stats'),
  // Assistant management
  listAssistants: () => api.get('/knowledge-base/assistants'),
  getAssistantInfo: (assistantName) => api.get(`/knowledge-base/assistants/${assistantName}/info`),
  deleteAssistant: (assistantName) => api.delete(`/knowledge-base/assistants/${assistantName}`),
  clearAssistantsCache: () => api.post('/knowledge-base/assistants/clear-cache'),
};

// KB Files Optimizer: turns rough .txt/.md files into structured KB files
export const kbOptimizer = {
  getModels: () => api.get('/knowledge-base/optimizer/models'),
  analyze: (formData) => api.post('/knowledge-base/optimizer/analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  startJob: (formData) => api.post('/knowledge-base/optimizer/jobs', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getJob: (jobId) => api.get(`/knowledge-base/optimizer/jobs/${jobId}`),
  extractText: (formData) => api.post('/knowledge-base/optimizer/extract', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    responseType: 'blob'
  }),
  downloadAll: (jobId) => api.get(`/knowledge-base/optimizer/jobs/${jobId}/download`, { responseType: 'blob' }),
};

// KB Web Scraper: website pages -> KB files (BrowserAct), optional AI pass
export const kbWebScraper = {
  getConfig: () => api.get('/knowledge-base/web-scraper/config'),
  discover: (url) => api.post('/knowledge-base/web-scraper/discover', { url }),
  startJob: (data) => api.post('/knowledge-base/web-scraper/jobs', data),
  getJob: (jobId) => api.get(`/knowledge-base/web-scraper/jobs/${jobId}`),
  cancelJob: (jobId) => api.post(`/knowledge-base/web-scraper/jobs/${jobId}/cancel`),
  optimize: (jobId, data) => api.post(`/knowledge-base/web-scraper/jobs/${jobId}/optimize`, data),
  downloadJob: (jobId, variant = 'formatted') => api.get(`/knowledge-base/web-scraper/jobs/${jobId}/download`, { params: { variant }, responseType: 'blob' }),
  downloadExtracted: (jobId) => api.get(`/knowledge-base/web-scraper/jobs/${jobId}/extracted`, { responseType: 'blob' }),
  listSites: () => api.get('/knowledge-base/web-scraper/sites'),
  getSite: (siteId) => api.get(`/knowledge-base/web-scraper/sites/${siteId}`),
  updateSite: (siteId, data) => api.put(`/knowledge-base/web-scraper/sites/${siteId}`, data),
  deleteSite: (siteId) => api.delete(`/knowledge-base/web-scraper/sites/${siteId}`),
  syncSite: (siteId, data) => api.post(`/knowledge-base/web-scraper/sites/${siteId}/sync`, data || {}),
  downloadSite: (siteId, variant = 'formatted') => api.get(`/knowledge-base/web-scraper/sites/${siteId}/download`, { params: { variant }, responseType: 'blob' }),
  reviewAgentKb: (siteId, params) => api.get(`/knowledge-base/web-scraper/sites/${siteId}/agent-kb/review`, { params }),
  replaceAgentKb: (siteId, data) => api.post(`/knowledge-base/web-scraper/sites/${siteId}/agent-kb/replace`, data),
  getAgentKbReplace: (siteId) => api.get(`/knowledge-base/web-scraper/sites/${siteId}/agent-kb/replace`),
  listChanges: () => api.get('/knowledge-base/web-scraper/changes'),
  checkSiteNow: (siteId) => api.post(`/knowledge-base/web-scraper/sites/${siteId}/monitor/check`),
  markSiteSeen: (siteId) => api.post(`/knowledge-base/web-scraper/sites/${siteId}/monitor/seen`),
  createSite: (data) => api.post('/knowledge-base/web-scraper/sites', data),
  sendMonitorTestEmail: (siteId) => api.post(`/knowledge-base/web-scraper/sites/${siteId}/monitor/test-email`),
};

// Developer Management API
export const developers = {
  login: (email, password, recaptchaToken, rememberMe = false, deliveryMethod = 'email') => api.post('/developer/login', { email, password, recaptcha_token: recaptchaToken, remember_me: rememberMe, delivery_method: deliveryMethod }),
  verify2FA: (verificationToken, code) => api.post('/developer/verify-2fa', { verification_token: verificationToken, code }),
  register: (data) => api.post('/developer/register', data),
  verifyInvitation: (token) => api.get(`/developer/verify-invitation/${token}`),
  verifyResetToken: (token) => api.get(`/developer/verify-reset-token/${token}`),
  resetPassword: (data) => api.post('/developer/reset-password', data),
  getMe: () => api.get('/developer/me'),
  updateMe: (data) => api.put('/developer/me', data),
  get2FAConfig: () => api.get('/developer/2fa-config'),
};

// Super Admin Auth API
export const superAdminAuth = {
  login: (email, password, recaptchaToken, rememberMe = false, deliveryMethod = 'email') => api.post('/super-admin/login', { email, password, recaptcha_token: recaptchaToken, remember_me: rememberMe, delivery_method: deliveryMethod }),
  verify2FA: (verificationToken, code) => api.post('/super-admin/verify-2fa', { verification_token: verificationToken, code }),
};

// Super Admin API
export const superAdmin = {
  // Organizations
  listOrganizations: () => api.get('/super-admin/organizations'),
  createOrganization: (data) => api.post('/super-admin/organizations', data),
  updateOrganization: (orgId, data) => api.put(`/super-admin/organizations/${orgId}`, data),
  deleteOrganization: (orgId) => api.delete(`/super-admin/organizations/${orgId}`),
  getOrganizationOverview: (orgId) => api.get(`/super-admin/organizations/${orgId}/overview`),
  clearOrgVendorKey: (orgId, keyType) => api.delete(`/super-admin/organizations/${orgId}/vendor-keys/${keyType}`),

  // Trial caller whitelist (per organization)
  listTrialCallers: (orgId) => api.get(`/super-admin/organizations/${orgId}/trial-callers`),
  addTrialCaller: (orgId, data) => api.post(`/super-admin/organizations/${orgId}/trial-callers`, data),
  updateTrialCaller: (orgId, callerId, data) => api.put(`/super-admin/organizations/${orgId}/trial-callers/${callerId}`, data),
  deleteTrialCaller: (orgId, callerId) => api.delete(`/super-admin/organizations/${orgId}/trial-callers/${callerId}`),

  // IP-based blocking (platform-wide, every public widget)
  listBlockedIps: () => api.get('/super-admin/blocked-ips'),
  addBlockedIp: (data) => api.post('/super-admin/blocked-ips', data),
  updateBlockedIp: (entryId, data) => api.put(`/super-admin/blocked-ips/${entryId}`, data),
  deleteBlockedIp: (entryId) => api.delete(`/super-admin/blocked-ips/${entryId}`),
  
  // Developers
  listDevelopers: () => api.get('/super-admin/developers'),
  inviteDeveloper: (data) => api.post('/super-admin/developers/invite', data),
  assignOrganization: (developerId, data) => api.post(`/super-admin/developers/${developerId}/assign-org`, data),
  updateDeveloperOrganizations: (developerId, data) => api.put(`/super-admin/developers/${developerId}/organizations`, data),
  removeOrganization: (developerId, orgId) => api.delete(`/super-admin/developers/${developerId}/assign-org/${orgId}`),
  updateDeveloper: (developerId, data) => api.put(`/super-admin/developers/${developerId}`, data),
  deleteDeveloper: (developerId) => api.delete(`/super-admin/developers/${developerId}`),
  toggleDeveloperAccess: (developerId, isActive) => api.patch(`/super-admin/developers/${developerId}/access`, { is_active: isActive }),
  sendPasswordReset: (developerId) => api.post(`/super-admin/developers/${developerId}/send-password-reset`),
  
  // Invitations
  listInvitations: () => api.get('/super-admin/invitations'),
  
  // Usage
  getUsage: (orgId, period, startDate, endDate) => {
    const params = new URLSearchParams();
    if (orgId) params.append('org_id', orgId);
    if (period) params.append('period', period);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return api.get(`/super-admin/usage?${params.toString()}`);
  },
  
  // Clients
  listClients: () => api.get('/super-admin/clients'),
  createClient: (data) => api.post('/super-admin/clients', data),
  updateClient: (clientId, data) => api.put(`/super-admin/clients/${clientId}`, data),
  deleteClient: (clientId) => api.delete(`/super-admin/clients/${clientId}`),
  updateClientOrganizations: (clientId, data) => api.put(`/super-admin/clients/${clientId}/organizations`, data),
};

// API Keys API
export const apiKeys = {
  list: () => api.get('/api-keys'),
  create: (data) => api.post('/api-keys', data),
  revoke: (keyId) => api.post(`/api-keys/${keyId}/revoke`),
  delete: (keyId) => api.delete(`/api-keys/${keyId}`),
};

// IVR QA API (DTMF Phase 2) - reporting on audited phone trees.
// The /ivr-qa/plan and /ivr-qa/calls/.../complete endpoints are called by the
// voice worker, not the browser, so they are deliberately absent here.
export const ivrQa = {
  listRuns: (targetNumber = null) => {
    const params = new URLSearchParams();
    if (targetNumber) params.append('target_number', targetNumber);
    const qs = params.toString();
    return api.get(`/ivr-qa/runs${qs ? `?${qs}` : ''}`);
  },
  createRun: (data) => api.post('/ivr-qa/runs', data),
  getRun: (runId) => api.get(`/ivr-qa/runs/${runId}`),
  // scope 'run' drops the run and its calls but keeps the learned map, so the
  // next call still resumes into the unexplored frontier. scope 'line' also
  // drops the map and every call for the number, which is the only thing that
  // makes exploration start from the root again. `force` (line only) first
  // stops every dialler on the number and hangs up its queued and live calls.
  deleteRun: (runId, scope = 'run', force = false) =>
    api.delete(`/ivr-qa/runs/${runId}?scope=${encodeURIComponent(scope)}${force ? '&force=true' : ''}`),
  // The dialler. `start` also carries the campaign settings, so setting up and
  // starting is one request. Resume re-validates before carrying on, since a
  // number can be reassigned while a run sits paused.
  startRun: (runId, settings = {}) => api.post(`/ivr-qa/runs/${runId}/start`, settings),
  pauseRun: (runId) => api.post(`/ivr-qa/runs/${runId}/pause`),
  resumeRun: (runId) => api.post(`/ivr-qa/runs/${runId}/resume`),
  stopRun: (runId) => api.post(`/ivr-qa/runs/${runId}/stop`),
  getCall: (callId) => api.get(`/ivr-qa/calls/${callId}`),
  getTree: (targetNumber) =>
    api.get(`/ivr-qa/tree?target_number=${encodeURIComponent(targetNumber)}`),
  // Absolute URL rather than an axios call: this is a file download, so it is
  // opened in a new tab and the browser handles the Content-Disposition.
  exportUrl: (runId) => `${API_BASE_URL}/ivr-qa/runs/${runId}/export.csv`,
  // The built-in QA prompt plus a description of what gets appended to it per
  // call. Read by the agent form so a tuned prompt starts from the tested one.
  defaultPrompt: () => api.get('/ivr-qa/default-prompt'),
};

export default api;
